import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { formatApiError } from "../lib/api";

const Field = ({ label, testid, ...props }) => (
  <label className="block">
    <span className="label-gold block mb-2">{label}</span>
    <input data-testid={testid} className="input-navy" {...props} />
  </label>
);

export default function Auth() {
  const [params, setParams] = useSearchParams();
  const mode = params.get("mode") === "register" ? "register" : "login";
  const { user, login, register } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => setError(""), [mode]);
  if (user) return <Navigate to={user.paid ? "/journal" : "/paywall"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = mode === "login" ? await login(form.email, form.password) : await register(form.email, form.password, form.name);
      nav(u.paid ? "/journal" : "/paywall");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="max-w-md mx-auto px-5 py-20 sm:py-28 fade-up">
      <div className="glass-card p-8 sm:p-10">
        <h1 className="font-serif text-4xl text-[#F4F6FA] mb-8" data-testid="auth-title">
          {mode === "login" ? t("auth_login_t") : t("auth_register_t")}
        </h1>
        <form onSubmit={submit} className="space-y-5" data-testid="auth-form">
          {mode === "register" && <Field label={t("auth_name")} testid="auth-name-input" value={form.name} onChange={set("name")} />}
          <Field label={t("auth_email")} testid="auth-email-input" type="email" required value={form.email} onChange={set("email")} />
          <Field label={t("auth_password")} testid="auth-password-input" type="password" required minLength={mode === "register" ? 8 : undefined} value={form.password} onChange={set("password")} />
          {mode === "register" && <p className="text-xs text-[#8E9BB4] -mt-2">{t("auth_pw_hint")}</p>}
          {error && <p className="text-sm text-red-400" data-testid="auth-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="auth-submit-btn" className="btn-gold w-full py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-60">
            {busy && <Loader2 size={16} className="animate-spin" />}
            {mode === "login" ? t("auth_login_btn") : t("auth_register_btn")}
          </button>
        </form>
        <button
          data-testid="auth-switch-mode-btn"
          onClick={() => setParams({ mode: mode === "login" ? "register" : "login" })}
          className="mt-6 text-sm text-[#8E9BB4] hover:text-[#E5C158] transition-colors w-full text-center"
        >
          {mode === "login" ? t("auth_switch_to_register") : t("auth_switch_to_login")}
        </button>
      </div>
    </div>
  );
}
